// Add this at the top of the file to extend the Window interface
declare global {
  interface Window {
    __darvin_lastMouseEvent?: MouseEvent;
  }
}

import { useEffect, useRef } from "react";
import { LuPanelLeftOpen } from "react-icons/lu";
import { useAuthUser } from "../../core_stores/useAuthStore";
import {
  SideMenuBehavior,
  useCanReopenSideMenu,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";

export default function SideMenuPanel() {
  const { open: openSideMenu, setCanReopenMenu } = useSideMenuActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const canReopenMenu = useCanReopenSideMenu();
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Listen for custom event to block reopening after close
  useEffect(() => {
    function handleBlockReopen() {
      // If cursor is over the panel, block reopening
      if (panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect();
        const { clientX, clientY } = window.__darvin_lastMouseEvent || {};
        if (
          typeof clientX === "number" &&
          typeof clientY === "number" &&
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          setCanReopenMenu(false);
        } else if (sideMenuBehavior === SideMenuBehavior.PINNED) {
          setCanReopenMenu(false);
        }
      }
    }
    window.addEventListener("darvin-block-reopen-menu", handleBlockReopen);
    return () => {
      window.removeEventListener("darvin-block-reopen-menu", handleBlockReopen);
    };
  }, [sideMenuBehavior]);

  // Track last mouse position globally
  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      window.__darvin_lastMouseEvent = e;
    }
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div
      ref={panelRef}
      onMouseEnter={() => {
        if (canReopenMenu) {
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          timeoutRef.current = setTimeout(() => {
            openSideMenu();
          }, 100);
        }
      }}
      onMouseLeave={() => {
        // Cancel the timeout if mouse leaves
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setCanReopenMenu(true);
      }}
      className={`${
        sideMenuBehavior === SideMenuBehavior.PINNED && "hidden"
      } h-full w-[56px] flex flex-col
      items-center justify-end fixed left-0 overflow-hidden
        bg-light-surface-primary dark:bg-dark-surface-primary
        transform transition-transform duration-300 ease-in-out z-40
        border-r border-light-border dark:border-dark-border`}
    >
      <div className="w-full flex flex-col items-center pb-6 gap-4">
        <Avatar />
        <button
          onClick={openSideMenu}
          className="w-10 h-10 flex items-center justify-center rounded-lg
            text-light-text-muted dark:text-dark-text-muted
            hover:text-light-text dark:hover:text-dark-text
            hover:bg-light-hover dark:hover:bg-dark-hover
            transition-colors duration-200"
        >
          <LuPanelLeftOpen size={22} />
        </button>
      </div>
    </div>
  );
}

function Avatar() {
  const user = useAuthUser();

  return (
    <div className="w-[28px] h-[28px] rounded-full overflow-hidden cursor-pointer hover:opacity-80 transition-opacity">
      <img
        src={user?.photoURL || ""}
        alt="User avatar"
        className="w-full h-full object-cover"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}
